import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetProvidersByAccountQuery } from "store/queries/providers";
import {
  useCreateSftpPartnerMutation,
  useGetSftpPartnerQuery,
  useUpdateSftpPartnerMutation,
} from "store/queries/sftpPartners";
import { EdiClaimType, IEdiUploadMapping } from "types/ediConversionFile";
import { IProvider } from "types/Provider/Provider";
import { ISftpPartnerDetail, ISftpPartnerRuleInput } from "types/SftpPartner";

import {
  ediMappingSpecialtyError,
  validateEdiMappingSet,
} from "../PayerRules/components/ediMappingValidation";
import MappingRuleFields, {
  posFilterAppliesTo,
} from "../PayerRules/components/MappingRuleFields";
import { SftpRuleSettings } from "./components/SftpPartnerParts";
import {
  humanizeEnum,
  RULE_HEADLINE_FIELDS,
  SFTP_PREFIX_RE,
  SFTP_SLUG_RE,
  SFTP_TAB_PATH,
  sftpApiErrorMessage,
  sftpPartnerPath,
} from "./utils/sftpPartners";

// Settings the server drops from a rule that is not Professional — Provider (they only apply there).
const PROFESSIONAL_ONLY_FIELDS = [
  "excludeMidlevels",
  "excludeBehavioralHealth",
  "payerTaxonomyRules",
];

interface IFormRule {
  key: number;
  mapping: IEdiUploadMapping;
  // Edit only: the stored rule this one came from. Sent as `sourceIndex` so the server keeps every
  // setting this form does not edit (payerTaxonomyRules, excludeMidlevels, …).
  sourceIndex?: number;
  stored?: Record<string, unknown>;
}

const ruleFromStored = (
  rule: Record<string, unknown>,
  index: number
): IFormRule => ({
  key: index,
  sourceIndex: index,
  stored: rule,
  mapping: {
    claimType: (rule.claimType as EdiClaimType) ?? "INSTITUTIONAL",
    providerId: typeof rule.providerId === "string" ? rule.providerId : "",
    placeOfServiceFilter: Array.isArray(rule.placeOfServiceFilter)
      ? (rule.placeOfServiceFilter as IEdiUploadMapping["placeOfServiceFilter"])
      : [],
  },
});

const newRule = (key: number): IFormRule => ({
  key,
  mapping: {
    claimType: "INSTITUTIONAL",
    providerId: "",
    placeOfServiceFilter: [],
  },
});

// What the form submits for one rule: the fields it edits, plus the stored rule it came from.
export const toRuleInput = (r: IFormRule): ISftpPartnerRuleInput => {
  const out: ISftpPartnerRuleInput = {
    claimType: r.mapping.claimType,
    providerId: r.mapping.providerId,
  };
  if (r.sourceIndex !== undefined) out.sourceIndex = r.sourceIndex;
  if (posFilterAppliesTo(r.mapping.claimType)) {
    const pos = r.mapping.placeOfServiceFilter ?? [];
    const storedHadPos =
      !!r.stored &&
      r.stored.claimType === r.mapping.claimType &&
      "placeOfServiceFilter" in r.stored;
    // Omit an untouched empty filter so an unchanged rule stays byte-for-byte the same.
    if (pos.length || storedHadPos) out.placeOfServiceFilter = pos;
  }
  return out;
};

const providerLabel = (p: IProvider) =>
  [p.name, p.npi ? `NPI ${p.npi}` : null, humanizeEnum(p.specialty)]
    .filter(Boolean)
    .join(" · ");

const otherSettings = (stored?: Record<string, unknown>) =>
  stored
    ? Object.keys(stored).filter((k) => !RULE_HEADLINE_FIELDS.includes(k))
    : [];

function HelpText({ children }: { children: any }) {
  return <small className="block text-600 mt-1">{children}</small>;
}

interface ISftpPartnerFormProps {
  // Absent = create.
  partner?: ISftpPartnerDetail;
}

// Create or edit an SftpIngestionPartner (SuperAdmin, Integrations → SFTP). The server runs the same
// mapping-rule validation SFTP ingestion runs; the checks here only flag problems before submit.
export function SftpPartnerForm({ partner }: ISftpPartnerFormProps) {
  const isEdit = !!partner;
  const navigate = useNavigate();
  const toastRef = useContext(ToastContext);
  const [createPartner, { isLoading: creating }] =
    useCreateSftpPartnerMutation();
  const [updatePartner, { isLoading: updating }] =
    useUpdateSftpPartnerMutation();
  const saving = creating || updating;

  const [slug, setSlug] = useState(partner?.slug ?? "");
  const [s3Prefix, setS3Prefix] = useState(partner?.s3Prefix ?? "");
  const [prefixTouched, setPrefixTouched] = useState(isEdit);
  const [accountId, setAccountId] = useState(partner?.accountId ?? "");
  const [includeTokens, setIncludeTokens] = useState<string[]>(
    partner?.includeTokens ?? []
  );
  const [rules, setRules] = useState<IFormRule[]>(() =>
    partner
      ? partner.mappingRules.map((m, i) => ruleFromStored(m.rule, i))
      : [newRule(0)]
  );
  const [nextKey, setNextKey] = useState(
    (partner?.mappingRules.length ?? 1) + 1
  );
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: accountsData, isFetching: accountsLoading } =
    useGetAllAccountsQuery();
  const { data: providersData, isFetching: providersLoading } =
    useGetProvidersByAccountQuery({ accountId }, { skip: !accountId });
  const providers: IProvider[] = accountId ? providersData?.data ?? [] : [];

  const accountOptions = useMemo(() => {
    const opts = (accountsData?.data ?? [])
      .map((a) => ({ label: a.name, value: a.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // The partner's account may be archived or gone; keep it selectable so the form shows it.
    if (partner && !opts.some((o) => o.value === partner.accountId)) {
      opts.unshift({
        label: partner.accountName ?? `${partner.accountId} (not found)`,
        value: partner.accountId,
      });
    }
    return opts;
  }, [accountsData, partner]);

  const accountLocked = isEdit && (partner?.filesProcessed ?? 0) > 0;
  const prefixChanged = isEdit && s3Prefix !== partner?.s3Prefix;

  const onSlugChange = (value: string) => {
    setSlug(value);
    if (!prefixTouched) setS3Prefix(value ? `${value}/` : "");
  };

  const onAccountChange = (value: string) => {
    setAccountId(value ?? "");
    // Providers belong to one account: a new account clears every rule's provider.
    setRules((prev) =>
      prev.map((r) => ({ ...r, mapping: { ...r.mapping, providerId: "" } }))
    );
  };

  const updateRule = (key: number, mapping: IEdiUploadMapping) =>
    setRules((prev) =>
      prev.map((r) => (r.key === key ? { ...r, mapping } : r))
    );

  // Client-side checks — the server repeats all of them and is the authority.
  const ruleErrors = new Map<number, string>();
  rules.forEach((r) => {
    if (!r.mapping.providerId) {
      ruleErrors.set(r.key, "Choose a provider.");
      return;
    }
    if (providersLoading) return;
    const specialtyError = ediMappingSpecialtyError(r.mapping, providers);
    if (specialtyError) ruleErrors.set(r.key, specialtyError);
    else if (!providers.some((p) => p.id === r.mapping.providerId))
      ruleErrors.set(
        r.key,
        "This provider is not one of the selected account's providers."
      );
  });
  const formErrors: string[] = [];
  if (!isEdit && (!SFTP_SLUG_RE.test(slug) || slug === "new"))
    formErrors.push(
      "Slug: lowercase letters, digits and single hyphens only (e.g. nmc or st-francis)."
    );
  if (!SFTP_PREFIX_RE.test(s3Prefix))
    formErrors.push(
      'S3 prefix must end with "/" (e.g. heritage/), with no leading "/" and no spaces.'
    );
  if (!accountId) formErrors.push("Choose an account.");
  if (!rules.length) formErrors.push("Add at least one mapping rule.");
  const setError = validateEdiMappingSet(rules.map((r) => r.mapping));
  if (setError) formErrors.push(setError);
  const canSubmit = !saving && formErrors.length === 0 && ruleErrors.size === 0;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setServerError(null);
    const body = {
      s3Prefix,
      accountId,
      includeTokens,
      mappingRules: rules.map(toRuleInput),
    };
    try {
      if (partner) {
        await updatePartner({ slug: partner.slug, body }).unwrap();
      } else {
        await createPartner({ slug, ...body }).unwrap();
      }
      const target = partner?.slug ?? slug;
      toastRef?.current?.show({
        severity: "success",
        summary: isEdit ? "Partner updated" : "Partner created",
        detail: isEdit
          ? `${target} was saved.`
          : `${target} was created disabled. Check it, then use Enable to start the daily job.`,
        life: 6000,
      });
      navigate(sftpPartnerPath(target));
    } catch (error) {
      setServerError(sftpApiErrorMessage(error, "Couldn't save the partner."));
    }
  };

  const cancelPath = partner ? sftpPartnerPath(partner.slug) : SFTP_TAB_PATH;

  return (
    <div className="flex flex-column gap-3" data-testid="sftp-partner-form">
      {serverError ? (
        <Message
          severity="error"
          text={serverError}
          data-testid="sftp-partner-form-error"
        />
      ) : null}

      <Message
        severity="info"
        text={
          "This page configures how a partner's files are ingested: which account they load into, " +
          "which files are read and which provider each claim routes to. It does not create the " +
          "partner's SFTP login, S3 folder or firewall rules — that is the AWS runbook in the wiki " +
          '(SFTP.md, "Adding a new partner").'
        }
      />

      <Card title="Partner">
        <div className="grid">
          <div className="col-12 md:col-6">
            <label htmlFor="sftp-slug" className="block font-semibold mb-2">
              Slug
            </label>
            <InputText
              id="sftp-slug"
              value={slug}
              onChange={(e) => onSlugChange(e.target.value)}
              disabled={isEdit || saving}
              placeholder="e.g. nmc"
              className="w-full"
            />
            <HelpText>
              Lowercase letters, digits and hyphens.{" "}
              {isEdit ? "It can't be changed:" : "It can't be changed later:"}{" "}
              the processed-file ledger and conversion names are keyed on it.
            </HelpText>
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="sftp-prefix" className="block font-semibold mb-2">
              S3 prefix
            </label>
            <InputText
              id="sftp-prefix"
              value={s3Prefix}
              onChange={(e) => {
                setPrefixTouched(true);
                setS3Prefix(e.target.value);
              }}
              disabled={saving}
              placeholder="e.g. nmc/"
              className="w-full"
            />
            <HelpText>
              The folder in s3://clearhealth-sftp/ the daily job reads, ending
              in &quot;/&quot;. Normally the slug followed by &quot;/&quot;.
            </HelpText>
            {prefixChanged ? (
              <Message
                severity="warn"
                className="mt-2"
                text={`Changing the prefix points the daily job at a different folder. Files in ${partner?.s3Prefix} will no longer be read.`}
              />
            ) : null}
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="sftp-account" className="block font-semibold mb-2">
              Account
            </label>
            <Dropdown
              inputId="sftp-account"
              value={accountId}
              options={accountOptions}
              onChange={(e) => onAccountChange(e.value)}
              placeholder={accountsLoading ? "Loading…" : "Select an account"}
              filter
              filterBy="label"
              className="w-full"
              disabled={accountLocked || saving}
              appendTo={document.body}
            />
            <HelpText>
              {accountLocked
                ? `The account can't be changed: ${partner?.filesProcessed} file(s) have already been processed for this partner under it.`
                : "Conversions and claims are created under this account. It can be changed only until the first file is processed."}
            </HelpText>
          </div>
          <div className="col-12 md:col-6">
            <div className="font-semibold mb-2">Mode</div>
            <div title="Pull partners need fetch code and a cron (a deploy), so they can't be created here. The mode can't be changed.">
              {partner?.ingestMode === "PULL" ? "Pull" : "Push"}
            </div>
            <HelpText>
              {isEdit
                ? "The mode can't be changed."
                : "Push: the partner uploads to our SFTP server. Pull partners need fetch code and a cron (a deploy), so they can't be created here."}
            </HelpText>
          </div>
          {!isEdit ? (
            <div className="col-12">
              <HelpText>
                New partners are created <strong>disabled</strong>. Check the
                partner&apos;s page, then use Enable to start the daily job.
              </HelpText>
            </div>
          ) : null}
        </div>
      </Card>

      <Card title="Include tokens">
        <Chips
          inputId="sftp-tokens"
          value={includeTokens}
          onChange={(e) =>
            setIncludeTokens([
              ...new Set(
                (e.value ?? [])
                  .map((t: string) => t.trim())
                  .filter((t: string) => t.length > 0)
              ),
            ] as string[])
          }
          separator=","
          placeholder="e.g. HMA, DDEC"
          disabled={saving}
          className="w-full"
        />
        <HelpText>
          Only files whose names carry one of these tokens are loaded. For 835s
          and files at the folder root, the token must equal the part of the
          file name before the first underscore (HMA for HMA_20261001.835); an
          837 matches when its name starts with the token. Leave empty to load
          every file.
        </HelpText>
      </Card>

      <Card title="Mapping rules">
        <HelpText>
          Rule changes apply to files loaded from now on. Files already loaded
          keep their mapping: fix those with the per-file Edit mapping action,
          then reprocess.
        </HelpText>
        {!accountId ? (
          <Message
            severity="info"
            className="mt-2"
            text="Choose an account to list its providers."
          />
        ) : null}
        <div className="flex flex-column gap-3 mt-3">
          {rules.map((r, index) => {
            const others = otherSettings(r.stored);
            const dropped =
              r.mapping.claimType !== "PROFESSIONAL_PROVIDER" &&
              others.some((k) => PROFESSIONAL_ONLY_FIELDS.includes(k));
            return (
              <div
                key={r.key}
                className="p-3 border-1 surface-border border-round"
                data-testid="sftp-rule"
              >
                <div className="flex justify-content-between align-items-center mb-2">
                  <span className="font-semibold">Rule {index + 1}</span>
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    className="p-button-text p-button-danger p-button-sm"
                    aria-label={`Remove rule ${index + 1}`}
                    disabled={saving}
                    onClick={() =>
                      setRules((prev) => prev.filter((x) => x.key !== r.key))
                    }
                  />
                </div>
                <MappingRuleFields
                  mapping={r.mapping}
                  onChange={(m) => updateRule(r.key, m)}
                  providers={providers}
                  disabled={saving || !accountId}
                  disabledClaimTypes={rules
                    .filter((x) => x.key !== r.key)
                    .map((x) => x.mapping.claimType)}
                  idPrefix={`sftp-rule-${r.key}`}
                  showTaxonomyFilter={false}
                  providerLabel={providerLabel}
                  keepSelectedProvider
                />
                {ruleErrors.has(r.key) ? (
                  <small
                    className="p-error block mt-2"
                    data-testid="sftp-rule-error"
                  >
                    {ruleErrors.get(r.key)}
                  </small>
                ) : null}
                {others.length ? (
                  <div className="mt-3 text-sm">
                    <div className="text-600">
                      Other settings — kept as they are (this form does not edit
                      them):
                    </div>
                    <SftpRuleSettings
                      rule={Object.fromEntries(
                        others.map((k) => [k, r.stored?.[k]])
                      )}
                      payerNames={partner?.payerNames ?? {}}
                    />
                    {dropped ? (
                      <Message
                        severity="warn"
                        className="mt-2"
                        text="Midlevel, behavioral-health and payer taxonomy settings only apply to Professional — Provider rules, so they are removed when this rule is saved as another claim type."
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <Button
          type="button"
          label="Add rule"
          icon="pi pi-plus"
          className="p-button-text mt-2"
          disabled={saving}
          onClick={() => {
            setRules((prev) => [...prev, newRule(nextKey)]);
            setNextKey((k) => k + 1);
          }}
        />
      </Card>

      {formErrors.length ? (
        <ul className="p-error m-0 pl-4" data-testid="sftp-form-errors">
          {formErrors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}

      <div className="flex gap-2 justify-content-end">
        <Button
          type="button"
          label="Cancel"
          className="p-button-text"
          onClick={() => navigate(cancelPath)}
          disabled={saving}
        />
        <Button
          type="button"
          label={isEdit ? "Save changes" : "Create partner"}
          icon="pi pi-check"
          loading={saving}
          disabled={!canSubmit}
          onClick={onSubmit}
          data-testid="sftp-partner-submit"
        />
      </div>
    </div>
  );
}

// `/integrations/sftp/new`
export function SftpPartnerNew() {
  useDocumentTitle("SFTP · New partner");
  return (
    <div className="p-4">
      <Link to={SFTP_TAB_PATH} className="inline-block mb-3">
        <i className="pi pi-arrow-left mr-2" />
        SFTP partners
      </Link>
      <h1 className="mt-0 mb-3">New SFTP partner</h1>
      <SftpPartnerForm />
    </div>
  );
}

// `/integrations/sftp/:slug/edit`
export function SftpPartnerEdit() {
  const { slug = "" } = useParams();
  useDocumentTitle(`SFTP · Edit ${slug}`);
  const { data, isLoading, isError } = useGetSftpPartnerQuery(slug, {
    skip: !slug,
  });
  const partner = data?.data;

  let body;
  if (isLoading) body = <ProgressSpinner />;
  else if (isError || !partner)
    body = (
      <Message
        severity="error"
        text="Couldn't load this SFTP partner. It may not exist."
      />
    );
  // Keyed on the slug so navigating between partners reseeds the form.
  else body = <SftpPartnerForm key={partner.slug} partner={partner} />;

  return (
    <div className="p-4">
      <Link to={sftpPartnerPath(slug)} className="inline-block mb-3">
        <i className="pi pi-arrow-left mr-2" />
        {slug}
      </Link>
      <h1 className="mt-0 mb-3">Edit {slug}</h1>
      {body}
    </div>
  );
}
